/**
 * @mention autocomplete dropdown for comment composer.
 * Shows party suggestions when user types @ in the input.
 * Compact version for 320px task pane.
 */

import { useEffect, useRef, useState } from 'react'
import type { NegotiationParty } from '../../services/negotiationsApi'

interface MentionAutocompleteProps {
  parties: NegotiationParty[]
  query: string
  onSelect: (party: NegotiationParty) => void
  onClose: () => void
  /** Position relative to input */
  position: { top: number; left: number }
}

export function MentionAutocomplete({
  parties,
  query,
  onSelect,
  onClose,
  position,
}: MentionAutocompleteProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  // Filter parties by query (match tag_name, party_label, or actor display_name)
  const filteredParties = parties.filter(party => {
    if (!query) return true
    const q = query.toLowerCase()
    return (
      party.tag_name.toLowerCase().includes(q) ||
      party.party_label.toLowerCase().includes(q) ||
      (party.actor?.display_name?.toLowerCase().includes(q) ?? false)
    )
  })

  // Reset selection when filtered list changes
  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, filteredParties.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        if (filteredParties[selectedIndex]) {
          onSelect(filteredParties[selectedIndex])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [filteredParties, selectedIndex, onSelect, onClose])

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current
    const selected = list?.children[selectedIndex] as HTMLElement
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (filteredParties.length === 0) {
    return null
  }

  return (
    <div
      className="fixed z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700
        rounded-lg shadow-lg max-h-36 overflow-y-auto min-w-[180px]"
      style={{
        top: Math.min(position.top, window.innerHeight - 160),
        left: Math.min(Math.max(position.left, 4), window.innerWidth - 190),
      }}
    >
      <div ref={listRef} className="py-0.5">
        {filteredParties.map((party, index) => (
          <button
            key={party.id}
            type="button"
            onClick={() => onSelect(party)}
            className={`w-full px-2 py-1.5 flex items-center gap-1.5 text-left text-[10px]
              ${index === selectedIndex
                ? 'bg-primary-50 dark:bg-primary-900/30'
                : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
              }`}
          >
            {/* Party avatar with color */}
            <span
              className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-medium text-white flex-shrink-0"
              style={{ backgroundColor: party.color || '#6b7280' }}
            >
              {(party.party_label || party.tag_name)[0]?.toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-neutral-800 dark:text-neutral-200 truncate">
                {party.party_label || party.actor?.display_name || party.tag_name}
              </div>
              <div className="text-[9px] text-neutral-500 dark:text-neutral-400">
                @{party.tag_name}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Hook to manage @mention state in a textarea/input.
 */
export function useMentionAutocomplete(
  content: string,
  cursorPosition: number
): {
  isOpen: boolean
  query: string
  mentionStart: number
} {
  // Find if we're currently in an @mention
  const beforeCursor = content.slice(0, cursorPosition)
  const mentionMatch = beforeCursor.match(/@(\w*)$/)

  if (mentionMatch) {
    return {
      isOpen: true,
      query: mentionMatch[1],
      mentionStart: mentionMatch.index!,
    }
  }

  return {
    isOpen: false,
    query: '',
    mentionStart: -1,
  }
}
